import { useEffect, useMemo, useState } from 'react';
import { makePostCall, PatientHeader } from '../../../utils/helper';
import { REPORT_CONFIG } from '../constants';
import type { Report, OrderDetails } from '../types';

/** CSS applied to the page-header-content wrapper when generating a new header. */
const PAGE_HEADER_STYLE =
  'display:block;width:100%;height:auto;background:white;font-size:10px;text-align:center;';

/** CSS applied to the page-footer-content wrapper when generating a new footer. */
const PAGE_FOOTER_STYLE =
  'display:block;width:90%;height:auto;background:transparent;border:none;padding:8px 10px;font-size:14px;margin:10px auto;text-align:left;color:#333;line-height:1.4;';

/**
 * Strip iframe-specific inline styles and attributes from a stored
 * header/footer so it renders correctly outside the iframe.
 * Old reports may contain `position:fixed`, `bottom:0`, `contenteditable`, etc.
 */
const sanitizeForExternalPlacement = (html: string): string => {
  const div = document.createElement('div');
  div.innerHTML = html;
  const el = div.querySelector('.page-header-content, .page-footer-content');
  if (el instanceof HTMLElement) {
    el.style.removeProperty('position');
    el.style.removeProperty('bottom');
    el.style.removeProperty('left');
    el.style.removeProperty('min-height');
    el.removeAttribute('contenteditable');
  }
  return div.innerHTML;
};

/**
 * Fetches header/footer config from backend and generates header/footer HTML.
 *
 * Returns two flavours of each:
 *  - `headerHtml` / `footerHtml`                 — for **internal** mode (null/empty when report already has stored values)
 *  - `externalHeaderHtml` / `externalFooterHtml`  — for **external** mode (always the full HTML, stored or generated)
 *
 * Used by both ReportEditor and SplitScreenEditor to eliminate duplicate logic.
 */
export const useHeaderFooter = (orderDetails: OrderDetails | null, currentReport: Report | null) => {
  const [footerTemplate, setFooterTemplate] = useState('');
  const [showFooter, setShowFooter] = useState(true);
  const [configLoaded, setConfigLoaded] = useState(false);

  const isHeaderFooterExternal = REPORT_CONFIG.HEADER_FOOTER_EXTERNAL;

  useEffect(() => {
    let cancelled = false;
    const loadConfig = async () => {
      try {
        const response = await makePostCall('/no-auth/report-header-config', { action: 'get' });
        if (cancelled) return;

        const config = response?.data?.config;
        const shouldShowFooter = config ? config.showFooter !== false : true;
        setShowFooter(shouldShowFooter);

        const hasStoredFooter = !!(currentReport && currentReport.pr_report_footer);

        if (shouldShowFooter && !hasStoredFooter) {
          setFooterTemplate(config?.footerTemplate || REPORT_CONFIG.DEFAULT_FOOTER_TEXT);
        } else {
          setFooterTemplate('');
        }
      } catch {
        if (cancelled) return;
        setShowFooter(true);
        const hasStoredFooter = !!(currentReport && currentReport.pr_report_footer);
        setFooterTemplate(hasStoredFooter ? '' : REPORT_CONFIG.DEFAULT_FOOTER_TEXT);
      } finally {
        if (!cancelled) setConfigLoaded(true);
      }
    };

    loadConfig();
    return () => { cancelled = true; };
  }, [currentReport]);

  // ── Internal-mode values (null/empty when report already has stored data) ──

  const headerHtml = useMemo(() => {
    if (!orderDetails) return null;
    if (currentReport && currentReport.pr_report_header) return null;
    const { patient } = orderDetails.pacs_order;
    return PatientHeader(patient, currentReport, orderDetails.pacs_order, orderDetails.assigned_radiologist_name);
  }, [orderDetails, currentReport]);

  const footerHtml = useMemo(() => {
    if (!showFooter) return '';
    if (currentReport && currentReport.pr_report_footer) return '';
    return footerTemplate;
  }, [currentReport, showFooter, footerTemplate]);

  // ── External-mode values (always available, stored or freshly generated) ──

  const externalHeaderHtml = useMemo(() => {
    if (!orderDetails) return '';
    if (currentReport?.pr_report_header) {
      return sanitizeForExternalPlacement(currentReport.pr_report_header);
    }
    const { patient } = orderDetails.pacs_order;
    const raw = PatientHeader(patient, currentReport, orderDetails.pacs_order, orderDetails.assigned_radiologist_name);
    if (!raw) return '';
    return `<div class="page-header-content" style="${PAGE_HEADER_STYLE}">${raw}</div>`;
  }, [orderDetails, currentReport]);

  const externalFooterHtml = useMemo(() => {
    if (!showFooter) return '';
    if (currentReport?.pr_report_footer) {
      return sanitizeForExternalPlacement(currentReport.pr_report_footer);
    }
    if (!footerTemplate) return '';
    return `<div class="page-footer-content" style="${PAGE_FOOTER_STYLE}">${footerTemplate}</div>`;
  }, [currentReport, showFooter, footerTemplate]);

  return {
    headerHtml, footerHtml, showFooter, configLoaded,
    externalHeaderHtml, externalFooterHtml, isHeaderFooterExternal,
  };
};
